"use client"

import { Button } from "@/components/ui/button"
import { SlidersHorizontal, LayoutGrid, Map, Compass } from "lucide-react"
import { cn } from "@/lib/utils"
import Image from "next/image"
import Link from "next/link"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { useRouter, usePathname } from "next/navigation"

interface SpotsToolbarProps {
  viewMode: "grid" | "map"
  onViewModeChange: (mode: "grid" | "map") => void
  onFilterToggle: () => void
  showFilters: boolean
}

export function SpotsToolbar({ viewMode, onViewModeChange, onFilterToggle, showFilters }: SpotsToolbarProps) {
  const { isMobile } = usePlatform()
  const router = useRouter()
  const pathname = usePathname()
  const isLandingPage = pathname === "/"

  const handleToggle = (mode: "grid" | "map") => {
    triggerHaptic('selection')
    onViewModeChange(mode)
  }

  const handleFilterToggle = () => {
    triggerHaptic('impact')
    onFilterToggle()
  }

  return (
    <div className={cn(
      "sticky z-40 px-4 md:px-0",
      isLandingPage ? "top-8" : "top-24"
    )}>
      <div className={cn(
        "max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 p-4 md:p-6",
        "bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl rounded-[2.5rem] border border-white/20 dark:border-slate-800",
        "shadow-2xl shadow-blue-900/10"
      )}>
        {/* Left: Logo & Filter toggle */}
        <div className="flex items-center gap-6">
          {isLandingPage && (
            <>
              <button
                onClick={() => {
                  triggerHaptic('impact')
                  router.push("/")
                }}
                className="flex items-center gap-3 group transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Image src="/spotstosurf-transparant.png" alt="Logo" width={40} height={40} className="object-contain" />
                </div>
                <div className="hidden lg:block text-left">
                  <h1 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tighter italic leading-none">SPOTS2SURF</h1>
                  <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 italic leading-none">Join the crew</p>
                </div>
              </button>

              <div className="h-8 w-px bg-slate-100 dark:bg-slate-800 hidden lg:block" />
            </>
          )}

          <Button
            variant={showFilters ? "default" : "outline"}
            size="lg"
            onClick={handleFilterToggle}
            className={cn(
              "h-14 px-8 rounded-2xl flex items-center gap-3 transition-all",
              "font-black uppercase tracking-widest text-[10px] italic",
              showFilters 
                ? "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 border-none"
                : "border-slate-100 dark:border-slate-800 text-slate-500 hover:text-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/20"
            )}
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span>Refine Spots</span>
          </Button>
          
          <div className={cn(
            "hidden flex-col",
            isLandingPage ? "xl:flex" : "md:flex"
          )}>
            <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tighter italic leading-none">Discover</h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic flex items-center gap-2">
              <Compass className="w-3 h-3 text-blue-500" />
              Found 100+ locations
            </p>
          </div>
        </div>

        {/* Right: View toggle & Auth */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 bg-slate-100/50 dark:bg-slate-800/50 p-1.5 rounded-2xl border border-slate-200/20">
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              size="sm"
              onClick={() => handleToggle("grid")}
              className={cn(
                "h-11 px-6 rounded-xl flex items-center gap-2 transition-all",
                "font-black uppercase tracking-widest text-[10px] italic",
                viewMode === "grid"
                  ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-white shadow-xl scale-[1.05]"
                  : "text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
              )}
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden sm:inline">Grid view</span>
            </Button>
            <Button
              variant={viewMode === "map" ? "default" : "ghost"}
              size="sm"
              onClick={() => handleToggle("map")}
              className={cn(
                "h-11 px-6 rounded-xl flex items-center gap-2 transition-all",
                "font-black uppercase tracking-widest text-[10px] italic",
                viewMode === "map"
                  ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-white shadow-xl scale-[1.05]"
                  : "text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
              )}
            >
              <Map className="w-4 h-4" />
              <span className="hidden sm:inline">Interactive map</span>
            </Button>
          </div>

          {isLandingPage && (
            <>
              <div className="h-8 w-px bg-slate-100 dark:bg-slate-800 hidden lg:block mx-2" />
              <div className="flex items-center gap-3">
                <Link 
                  href="/auth/login" 
                  className="hidden sm:block text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-blue-600 transition-colors italic px-2"
                >
                  Sign In
                </Link>
                <Button
                  onClick={() => router.push("/auth/register")}
                  className="h-12 px-6 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-black uppercase tracking-widest text-[10px] shadow-lg shadow-blue-600/20 active:scale-[0.98] transition-all border-none italic"
                >
                  Join the Crew
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
